import type { UseStoredWidthParams, UseStoredWidthReturn } from './index.types';
import * as React from 'react';
import { clampWidth, readStoredWidth, writeStoredWidth } from '../../utils';

/**
 * Owns pixel width state backed by localStorage.
 * `setWidth` updates live (e.g. while dragging); `persistWidth` writes to storage.
 * Width is read once on mount. Remount (e.g. `key={storageKey}`) to load a different key.
 */
export const useStoredWidth = ({
  storageKey,
  bounds,
}: UseStoredWidthParams): UseStoredWidthReturn => {
  const [width, setWidthState] = React.useState(() =>
    readStoredWidth(storageKey, bounds)
  );
  const widthRef = React.useRef(width);

  const setWidth = React.useCallback(
    (nextWidth: number) => {
      const clamped = clampWidth(nextWidth, bounds);
      widthRef.current = clamped;
      setWidthState(clamped);
    },
    [bounds]
  );

  const persistWidth = React.useCallback(
    (nextWidth?: number) => {
      const value =
        nextWidth === undefined
          ? widthRef.current
          : clampWidth(nextWidth, bounds);

      widthRef.current = value;
      setWidthState(value);
      writeStoredWidth(storageKey, value);
    },
    [storageKey, bounds]
  );

  return { width, widthRef, setWidth, persistWidth };
};
